import React from 'react';
import { Spin } from 'antd';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { useProjectBlockersTab } from '@hooks/projects/useProjectBlockers';
import BlockersKanbanBoard from '@/components/module/projects/blockers_tab/BlockersKanbanBoard';
import BlockerCreateModal from '@/components/module/projects/blockers_tab/BlockerCreateModal';
import BlockerDetailModal from '@/components/module/projects/blockers_tab/BlockerDetailModal';
import {
    EmptyStateBlock,
    DetailPanelLoading,
    BoardKanbanBoardShell,
    BoardTabRoot,
    BoardPageTitle,
    BoardPageSubtitle,
} from '../style';

export default function ProjectBlockersTab({ projectId, project }) {
        const {
        activeBlockers,
        resolvedBlockers,
        isLoading,
        blockerModal,
        dropTarget,
        draggingColumn,
        handleDndDragStart,
        handleDndDragOverColumn,
        handleDndDragEnd,
        clearDropTarget,
        selectedBlocker,
        openBlockerDetail,
        closeBlockerDetail,
        blockerDiscussions,
        blockerDiscussionsLoading,
        createBlockerPost,
        createBlockerPostPending,
        editBlockerPost,
        editBlockerPostPending,
        deleteBlockerPost,
        deleteBlockerPostPending,
        canCreateBlocker,
        canDragBlockers,
    } = useProjectBlockersTab(projectId, project);

    const mainContent = (
        <BoardTabRoot
            style={{
                flex: 1,
                minWidth: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                marginBottom: 0,
                paddingBottom: 0,
            }}
        >
            <BoardTabRoot
                style={{
                    margin: '26px clamp(16px, 4vw, 40px) 0 clamp(16px, 4vw, 40px)',
                    marginBottom: 0,
                    padding: 0,
                    paddingBottom: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    '--board-kanban-inline-gutter': 'clamp(16px, 4vw, 40px)',
                }}
            >
                <Container
                    $display="flex"
                    $justifyContent="space-between"
                    $alignItems="flex-start"
                    $gap="16px"
                    $flexWrap="wrap"
                    style={{ marginBottom: 24 }}
                >
                    <div>
                        <BoardPageTitle>Blockers</BoardPageTitle>
                        <BoardPageSubtitle>
                            Track and resolve issues that block delivery for this project.
                        </BoardPageSubtitle>
                    </div>
                </Container>

                <BoardKanbanBoardShell>
                    <Container
                        $display="flex"
                        $flexDirection="column"
                        $flex="1"
                        $minHeight="0"
                        $overflow="hidden"
                        style={{ width: '100%' }}
                    >
                        <BlockersKanbanBoard
                            activeBlockers={activeBlockers}
                            resolvedBlockers={resolvedBlockers}
                            onAddBlocker={canCreateBlocker ? blockerModal.openModal : undefined}
                            onBlockerSelect={openBlockerDetail}
                            canDragBlockers={canDragBlockers}
                            dropTarget={dropTarget}
                            draggingColumn={draggingColumn}
                            onDndDragStart={handleDndDragStart}
                            onDndDragOverColumn={handleDndDragOverColumn}
                            onDndDragEnd={handleDndDragEnd}
                        />
                    </Container>
                </BoardKanbanBoardShell>
            </BoardTabRoot>
        </BoardTabRoot>
    );

    return (
        <>
            {isLoading ? (
                <Container
                    style={{
                        margin: '26px clamp(16px, 4vw, 40px) 0 clamp(16px, 4vw, 40px)',
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <EmptyStateBlock>
                        <DetailPanelLoading>
                            <Spin size="large" />
                        </DetailPanelLoading>
                    </EmptyStateBlock>
                </Container>
            ) : (
                <Container $display="flex" $flex="1" $minHeight="0" $overflow="hidden">
                    {mainContent}
                </Container>
            )}

            <BlockerDetailModal
                projectId={projectId}
                blocker={selectedBlocker}
                discussions={blockerDiscussions}
                discussionsLoading={blockerDiscussionsLoading}
                createPost={createBlockerPost}
                createPostPending={createBlockerPostPending}
                editPost={editBlockerPost}
                editPostPending={editBlockerPostPending}
                deletePost={deleteBlockerPost}
                deletePostPending={deleteBlockerPostPending}
                onClose={closeBlockerDetail}
            />

            <BlockerCreateModal
                form={blockerModal.form}
                open={blockerModal.modalOpen}
                onCancel={blockerModal.closeModal}
                onSubmit={blockerModal.handleCreateSubmit}
                submitPending={blockerModal.createBlockerPending}
                blockerPendingAttachments={blockerModal.blockerPendingAttachments}
                pendingLinkAttachments={blockerModal.pendingLinkAttachments}
                handleBlockerFilesSelected={blockerModal.handleBlockerFilesSelected}
                removeBlockerAttachment={blockerModal.removeBlockerAttachment}
                handleLinkRequest={blockerModal.handleLinkRequest}
                handleLinkModalSubmit={blockerModal.handleLinkModalSubmit}
                linkModalOpen={blockerModal.linkModalOpen}
                setLinkModalOpen={blockerModal.setLinkModalOpen}
                linkModalForm={blockerModal.linkModalForm}
                linkModalCallbackRef={blockerModal.linkModalCallbackRef}
                mentionUsers={blockerModal.mentionUsers}
                mentionMilestones={blockerModal.mentionMilestones}
                mentionSprints={blockerModal.mentionSprints}
                truncateFileName={blockerModal.truncateFileName}
            />
        </>
    );
}
